<template>
    <div class="editor-fields">
        <div
            ref="track"
            class="editor-fields__track"
            @click.capture="onClick"
            @focusin="onFocus"
            @scroll.passive="updateEnds"
        >
            <slot :compact="isCarousel" />
        </div>
        <div v-if="isCarousel" class="editor-fields__navs">
            <button
                v-show="!atStart"
                type="button"
                class="editor-fields__nav"
                :aria-label="$t('editor.fields.previous')"
                @click="step(-1)"
            >
                <i class="ri-arrow-left-s-line"></i>
            </button>
            <button
                v-show="!atEnd"
                type="button"
                class="editor-fields__nav"
                :aria-label="$t('editor.fields.next')"
                @click="step(1)"
            >
                <i class="ri-arrow-right-s-line"></i>
            </button>
        </div>
    </div>
</template>

<script>
// Editor form fields: a column on desktop, a swipeable row of snapping cards below lg (see _editor.pcss).
// On the row a tap on a card that is not centred only centres it. Opening a dropdown there would be
// useless anyway: PrimeVue closes an open overlay as soon as a parent scrolls.
const CENTER_TOLERANCE = 12;

export default {
    name: 'EditorFields',
    // Fields inside ask `editorFields.isCarousel` whether their popups must leave the row (see Input, FontFamilySelector).
    provide() {
        return { editorFields: this };
    },
    data() {
        return { isCarousel: false, atStart: true, atEnd: false };
    },
    mounted() {
        this.updateEnds();
        window.addEventListener('resize', this.updateEnds);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateEnds);
    },
    methods: {
        cards() {
            return [...this.$refs.track.children].filter((el) => el.offsetWidth);
        },
        cardOf(target) {
            let el = target;
            while (el && el.parentElement !== this.$refs.track) el = el.parentElement;
            return el;
        },
        offsetFromCenter(card) {
            const track = this.$refs.track.getBoundingClientRect();
            const rect = card.getBoundingClientRect();
            return rect.left + rect.width / 2 - (track.left + track.width / 2);
        },
        // false when the card is already as centred as the row can scroll it (e.g. the first card)
        center(card) {
            const track = this.$refs.track;
            const max = track.scrollWidth - track.clientWidth;
            const target = Math.min(Math.max(track.scrollLeft + this.offsetFromCenter(card), 0), max);
            if (Math.abs(target - track.scrollLeft) <= CENTER_TOLERANCE) return false;
            track.scrollTo({ left: target, behavior: 'smooth' });
            return true;
        },
        onClick(event) {
            if (!this.isCarousel) return;
            const card = this.cardOf(event.target);
            if (card && this.center(card)) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        // keyboard / programmatic focus (e.g. tabbing between inputs) brings the card into the middle
        onFocus(event) {
            if (!this.isCarousel) return;
            const card = this.cardOf(event.target);
            if (card) this.center(card);
        },
        step(direction) {
            const cards = this.cards();
            const current = cards.reduce(
                (best, card, index) =>
                    Math.abs(this.offsetFromCenter(card)) < Math.abs(this.offsetFromCenter(cards[best])) ? index : best,
                0
            );
            const next = cards[Math.min(Math.max(current + direction, 0), cards.length - 1)];
            if (next) this.center(next);
        },
        updateEnds() {
            const track = this.$refs.track;
            if (!track) return;
            this.isCarousel = track.scrollWidth > track.clientWidth + 1;
            this.atStart = track.scrollLeft <= 2;
            this.atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        },
    },
};
</script>
